import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled, { useTheme } from 'styled-components';
import styles from '@rsc/ui-components/styles';
import breakpoints from '@rsc/ui-components/breakpoints';
import BlockContent from '../../BlockContent';

const Container = styled.div`
  margin-inline: auto;
  max-width: ${breakpoints.lg};
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  line-height: 1.15;
  @media (min-width: ${breakpoints.lg}) {
    font-size: ${styles.fontSize.lg};
  }

  td,
  tr,
  th {
    padding: ${styles.spacing['spacing-1xs']};
    @media (min-width: ${breakpoints.lg}) {
      padding: ${styles.spacing['spacing-sm']};
    }
    text-align: center;
  }

  tr {
    th,
    td {
      border: 1px solid
        ${({ theme }) => theme.colors.comparisonChart.cellBorder};
      width: ${({ colCount }) => (100 - 40) / (colCount - 1)}%;
      @media (max-width: ${parseInt(breakpoints.md, 10) - 1}px) {
        width: ${({ colCount }) => 100 / colCount - 1}%;
      }

      img {
        display: inline-block;
        max-height: 50px;
        max-width: 100%;
        margin: 0;
      }

      &:first-child {
        @media (min-width: ${breakpoints.md}) {
          padding-inline: ${styles.spacing['spacing-md']};
          text-align: left;
          width: 40%;
        }
      }

      &:not(:first-child) {
        text-align: center;
      }

      &.hidden-sm {
        @media (max-width: ${parseInt(breakpoints.md, 10) - 1}px) {
          display: none;
        }
      }

      &.justify-left {
        div {
          justify-content: flex-start;
        }
      }
    }
  }

  thead {
    font-size: ${styles.fontSize.lg};
    @media (min-width: ${breakpoints.lg}) {
      font-size: ${styles.fontSize['1xl']};
    }

    tr {
      th {
        font-weight: bold;
        color: ${({ theme }) => theme.colors.brandPrimary};

        &[colspan] {
          @media (max-width: ${parseInt(breakpoints.md, 10) - 1}px) {
            text-align: center;
          }
        }
      }
    }
  }

  tbody {
    tr {
      &:nth-child(4n + 2) {
        @media (min-width: ${breakpoints.md}) {
          background-color: ${({ theme, alternateRowColors }) =>
            alternateRowColors
              ? theme.colors.comparisonChart.alternateRowShade
              : 'inherit'};
        }
      }

      &.visible-sm {
        background-color: ${({ theme }) =>
          theme.colors.comparisonChart.mobileSubhead.backgroundColor};
        @media (min-width: ${breakpoints.md}) {
          display: none;
        }
      }

      td {
        &.colHighlight-primary {
          background-color: ${({ theme }) =>
            theme.colors.comparisonChart.highlightColumn.primary
              .backgroundColor};
          color: ${({ theme }) =>
            theme.colors.comparisonChart.highlightColumn.primary.color};

          a {
            color: ${({ theme }) =>
              theme.colors.comparisonChart.highlightColumn.primary.color};
          }
        }
        &.colHighlight-secondary {
          background-color: ${({ theme }) =>
            theme.colors.comparisonChart.highlightColumn.secondary
              .backgroundColor};
          color: ${({ theme }) =>
            theme.colors.comparisonChart.highlightColumn.secondary.color};

          a {
            color: ${({ theme }) =>
              theme.colors.comparisonChart.highlightColumn.secondary.color};
          }
        }
        &.colHighlight-tertiary {
          background-color: ${({ theme }) =>
            theme.colors.comparisonChart.highlightColumn.tertiary
              .backgroundColor};
          color: ${({ theme }) =>
            theme.colors.comparisonChart.highlightColumn.tertiary.color};
          a {
            color: ${({ theme }) =>
              theme.colors.comparisonChart.highlightColumn.tertiary.color};
          }
        }
      }
    }
  }
`;

const CellText = styled.div`
  display: flex;
  justify-content: center;

  p strong {
    font-weight: 700;
  }

  > :last-child {
    margin-bottom: 0;
  }
`;

function outputCellContent(cell, getInternalLink) {
  return (
    cell.text?.length &&
    cell.text.map(text => (
      <CellText key={text._key}>
        <BlockContent blocks={text} getInternalLink={getInternalLink} />
      </CellText>
    ))
  );
}

function BlockBuilderComparisonChart({ value, getInternalLink }) {
  const theme = useTheme();

  const { highlightedColumnNum, themeStyle, alternateRowColors, table } = value;

  const { heading, rows } = table?.length ? table[0] : {};

  const [colCount, setColCount] = useState(0);

  return (
    <Container themeStyle={theme?.colors?.contentCard?.[themeStyle]}>
      <Table colCount={colCount} alternateRowColors={alternateRowColors}>
        <thead>
          {heading && (
            <tr>
              <th colSpan={colCount}>{heading}</th>
            </tr>
          )}
          {rows?.length > 0 && rows[0].cells?.length && (
            <tr>
              {rows[0].cells.map((cell, colIndex) => (
                <th
                  key={cell._key}
                  className={colIndex === 0 ? 'hidden-sm' : ''}
                >
                  {outputCellContent(cell, getInternalLink)}
                </th>
              ))}
            </tr>
          )}
        </thead>
        <tbody>
          {rows?.length > 1 &&
            rows.map((row, rowIndex) => {
              if (rowIndex === 0) return '';
              return (
                <>
                  {rowIndex > 0 && row.cells?.length > 0 && (
                    <tr key={`${row._key}-sm`} className="visible-sm">
                      <td colSpan={colCount - 1}>
                        {outputCellContent(row.cells[0], getInternalLink)}
                      </td>
                    </tr>
                  )}
                  <tr key={row._key}>
                    {row.cells?.length > 0 &&
                      row.cells.map((cell, colIndex) => {
                        if (colIndex + 1 > colCount) setColCount(colIndex + 1);

                        let cellClass =
                          themeStyle !== 'none' &&
                          colIndex + 1 === highlightedColumnNum
                            ? `colHighlight-${themeStyle}`
                            : '';

                        if (colIndex === 0)
                          cellClass += 'justify-left hidden-sm';
                        return (
                          <td key={cell._key} className={cellClass}>
                            {outputCellContent(cell, getInternalLink)}
                          </td>
                        );
                      })}
                    {row.cells?.length < colCount &&
                      Array.from({
                        length: colCount - row.cells.length,
                      }).map((item, index) => (
                        // eslint-disable-next-line jsx-a11y/control-has-associated-label
                        <td key={`emptyCell-${colCount + index}`} />
                      ))}
                  </tr>
                </>
              );
            })}
        </tbody>
      </Table>
    </Container>
  );
}

BlockBuilderComparisonChart.propTypes = {
  value: PropTypes.shape({
    heading: PropTypes.string,
    highlightedColumnNum: PropTypes.number,
    themeStyle: PropTypes.string,
    alternateRowColors: PropTypes.bool,
    table: PropTypes.arrayOf({
      _key: PropTypes.string,
      rows: PropTypes.arrayOf(
        PropTypes.shape({
          _key: PropTypes.string,
          cells: PropTypes.arrayOf(
            PropTypes.shape({
              _key: PropTypes.string,
              text: PropTypes.arrayOf(
                PropTypes.shape({ _key: PropTypes.string })
              ),
            })
          ),
        })
      ),
    }),
  }),

  getInternalLink: PropTypes.func.isRequired,
};

BlockBuilderComparisonChart.defaultProps = {
  value: {
    heading: '',
    highlightedColumnNum: 2,
    themeStyle: 'none',
    alternateRowColors: false,
    rows: [],
  },
};

export default BlockBuilderComparisonChart;
